import { memo, useMemo } from 'react'
import { useActiveChatId, useIsDummyChat, useIsTurnLive } from '../../../../store'
import type { ChatMessage } from '../../../../types'
import { groupCompletedMessages } from '../../../../utils/messageGrouping'
import { DummyMessages } from '../../message/DummyChat/DummyMessages'
import { Message } from '../../message'
import { StreamingLoader } from '../../message/nested/loader'
import { LatestStagedActionContext, StagedActionsOwnedByPriorTurnContext } from '../../latest-staged-actions-context'
import { useStagedActionIndex } from '../hooks/use-staged-action-index'

export type MessageListCallbacks = {
  onStagedActionClick?: (actionId: string, actionType: string) => void
  onStagedActionCreated?: () => void
  onReminderClick?: (reminderId: string) => void
}

export const RenderedMessages = memo(function RenderedMessages({
  messages,
  onStagedActionClick,
  onStagedActionCreated,
  onReminderClick,
}: MessageListCallbacks & {
  messages: Array<ChatMessage>
}) {
  const isDummyChat = useIsDummyChat()
  const activeChatId = useActiveChatId()

  const messageGroups = useMemo(() => groupCompletedMessages(messages), [messages])

  // A resumed turn (verify + re-subscribe after a hard refresh) has no
  // assistant row to host AssistantBody's loader until the stream's first
  // assistant event lands — the submit path's optimistic placeholder only
  // exists in the session that submitted. Host a trailing loader here for
  // that window. Gated by chat id so the liveness subscription only exists
  // while the transcript actually lacks a host row.
  const lastGroup = messageGroups[messageGroups.length - 1]
  const lacksLoaderHost = lastGroup === undefined || lastGroup.primaryMessage.role !== 'assistant'
  const showResumeLoader = useIsTurnLive(lacksLoaderHost ? activeChatId : undefined)

  const { latestStagedActionByActionId, stagedActionsOwnedByPriorTurn, lastUserGroupIndex } =
    useStagedActionIndex(messageGroups)

  return (
    <LatestStagedActionContext.Provider value={latestStagedActionByActionId}>
      <StagedActionsOwnedByPriorTurnContext.Provider value={stagedActionsOwnedByPriorTurn}>
        {isDummyChat && messages.length === 0 ? (
          <div className="text-foreground flex flex-col justify-end">
            <DummyMessages />
          </div>
        ) : (
          // shrink-0 keeps the flex-column scroll parent (`.message-body`)
          // from squeezing content taller than the viewport down to the
          // container height instead of letting it overflow into scroll room.
          <div className="shrink-0">
            {messageGroups.map((group, index) => (
              <Message
                key={group.id}
                group={group}
                isMostRecent={index === messageGroups.length - 1}
                isLastUserGroup={group.primaryMessage.role === 'user' && index === lastUserGroupIndex}
                onStagedActionClick={onStagedActionClick}
                onStagedActionCreated={onStagedActionCreated}
                onReminderClick={onReminderClick}
              />
            ))}
            {showResumeLoader && activeChatId !== undefined && (
              <div className="px-4">
                <StreamingLoader show conversationId={activeChatId} label="Resuming" />
              </div>
            )}
          </div>
        )}
      </StagedActionsOwnedByPriorTurnContext.Provider>
    </LatestStagedActionContext.Provider>
  )
})
